// Shared Tailwind class strings for the Tailwind-lane surfaces (sign-in,
// picker, chat, the readiness card, the session rails).
//
// Built ONLY from the role utilities `styles.css` generates off the `--ui-*`
// tokens (`bg-surface`, `text-ink-muted`, `border-line-strong`, …), so one
// string themes in both light and dark. Each light value is byte-identical to
// the copies it replaces; the comments name those call sites by file and role,
// never by the palette classes they used to spell (the palette guard reads
// source).

/** The small outlined chip button: a surface fill on a strong line, ink-3
 *  text, a surface-3 hover. Replaces the readiness card's own chip constant and
 *  the agent-ask pin's identical one (its concern buttons; the lone
 *  `mt-2`-prefixed copy is `mt-2 ${CHIP_BTN}`). */
export const CHIP_BTN =
  "rounded border border-line-strong bg-surface px-2 py-1 text-xs text-ink-3 hover:bg-surface-3";

/** The small inverse "send" button: an inverse fill with on-solid text, the
 *  disabled fill when empty. Replaces the readiness card's send constant. */
export const SEND_BTN =
  "rounded bg-inverse px-2 py-1 text-xs font-medium text-on-solid disabled:cursor-not-allowed disabled:bg-disabled";

/** The small text field: a strong line that turns focus-line on focus, no
 *  outline. The readiness card's text input is `flex-1 ${FIELD_SM}`. */
export const FIELD_SM =
  "rounded border border-line-strong px-2 py-1 text-xs focus:border-line-focus focus:outline-none";

/** The underlined text-link button: muted ink, full ink on hover. Replaces the
 *  readiness card's link constant. */
export const LINK_BTN = "text-xs text-ink-muted underline hover:text-ink";

/** The regular text field with the primary focus ring. Replaces the project
 *  picker's filter input (`mb-4 w-full ${FIELD}`) and the chat composer
 *  (`flex-1 ${FIELD}`). */
export const FIELD =
  "rounded-md border border-line-strong px-3 py-2 text-sm focus:border-primary-focus focus:outline-none focus:ring-1 focus:ring-primary-focus";

/** The confirm-family solid button. Replaces the idle-reconnect dialog's two
 *  buttons (`mt-4 w-full ${CONFIRM_BTN}`) and the assistant turn's Accept
 *  button (`mt-2 ${CONFIRM_BTN}` plus its own disabled state). */
export const CONFIRM_BTN =
  "rounded-md bg-confirm-solid px-3 py-1.5 text-sm font-medium text-on-solid hover:bg-confirm-solid-hover";

/** The dropdown menu panel: a surface card on a line, with the Tailwind
 *  medium shadow and its elevation colour (`shadow-elev` is what keeps it
 *  visible in dark). Replaces the sandbox-status menu (`absolute left-0
 *  top-full min-w-[10rem] text-sm ${MENU_PANEL}`) and the session sidebar's
 *  row menu (`absolute right-0 w-32 ${MENU_PANEL}`). */
export const MENU_PANEL =
  "z-10 mt-1 rounded-md border border-line bg-surface py-1 shadow-md shadow-elev";

/** One row of `MENU_PANEL`: full width, left-aligned, a surface-3 hover. */
export const MENU_ITEM = "block w-full px-3 py-1 text-left hover:bg-surface-3";

export type BadgeTone =
  | "neutral"
  | "success"
  | "pass"
  | "caution"
  | "danger"
  | "info"
  | "confirm"
  | "primary";

/** Pill / badge colours: each family's second soft fill with its second
 *  foreground, and a surface-3 / ink-3 neutral. Replaces the tool-call card's
 *  status badge, the sandbox-status tone map, the session sidebar's live / MR /
 *  code pills, the history sidebar's live pill and the readiness card's header
 *  badge (pass / caution). Colour only — each call site keeps its own shape. */
export const BADGE_TONE: Readonly<Record<BadgeTone, string>> = {
  neutral: "bg-surface-3 text-ink-3",
  success: "bg-success-soft-2 text-success-fg-2",
  pass: "bg-pass-soft-2 text-pass-fg-2",
  caution: "bg-caution-soft-2 text-caution-fg-2",
  danger: "bg-danger-soft-2 text-danger-fg-2",
  info: "bg-info-soft-2 text-info-fg-2",
  confirm: "bg-confirm-soft-2 text-confirm-fg-2",
  primary: "bg-primary-soft-2 text-primary-fg-2",
};
